import {
  PrimaryButton,
  SecondaryButton,
  Spinner,
} from "@graffer-inc/console-design-kit";
import React, { useState } from "react";
import type { Auth } from "../../../../application/domain/auth/auth";
import {
  Procedure,
  ProcedureDetail,
  Status,
  StatusValue,
} from "../../../../application/domain/proc-management";
import { ProcedureEntity } from "../../../../application/domain/procedures/procedure-entity";
import { useModal } from "../../../../utils/custom-hooks";
import { BaseModal } from "../../../atoms/base-modal";
import { UrlCopyButton } from "../../../atoms/proc-management/url-copy-button";
import { RBACHidden } from "../../../helpers/rbac/rbac";
import { ProcedureCopyModal } from "./procedure-copy-modal";
import { PublishModal } from "./publish-modal";
import { ProcedureDetailsGroupPermissionAttached } from "../../../../application/domain/proc-management/core/proc-management-procedure-details";
import { useAuthContextV2 } from "../../../../auth-state";

const statusPermsMap = {
  [Status.Draft]: {
    publish: "proc_management.procedures.update_status_from_draft_to_published",
    archive: "proc_management.procedures.update_status_from_draft_to_archived",
    copy: "proc_management.procedures.copy",
  },
  [Status.Published]: {
    unPublish:
      "proc_management.procedures.update_status_from_published_to_draft",
    copy: "proc_management.procedures.copy",
  },
  [Status.Archived]: {
    unPublish:
      "proc_management.procedures.update_status_from_archived_to_draft",
    copy: "proc_management.procedures.copy",
  },
} as const;

export const anyAction = (auth: Auth, status: StatusValue): boolean => {
  for (const perm of Object.values(statusPermsMap[status])) {
    if (auth.hasPermissions(perm)) {
      return true;
    }
  }
  return false;
};

export const StatusControl: React.FC<{
  orgId: string;
  procedure: Procedure | ProcedureDetail;
  procName?: string;
  smartApply?: Pick<ProcedureEntity, "id" | "publishStatus" | "orgId">;
  isServiceProviderAccount: boolean;
  currentStatus: StatusValue;
  publishUrl: string;
  publish: () => Promise<void>;
  unPublish: () => Promise<void>;
  archive: () => Promise<void>;
  copy: (name: string) => Promise<string | undefined>;
  publishProcAndSmartApply: () => Promise<void>;
  fetchPreviewUrl: () => Promise<string>;
}> = ({
  orgId,
  procedure,
  procName,
  smartApply,
  isServiceProviderAccount,
  currentStatus,
  publishUrl,
  publish,
  unPublish,
  archive,
  copy,
  publishProcAndSmartApply,
  fetchPreviewUrl,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const perms = statusPermsMap[currentStatus];
  const smartApplyStatus = smartApply?.publishStatus;
  const isUsePublishModal =
    smartApplyStatus &&
    currentStatus === "DRAFT" &&
    (smartApplyStatus === "DRAFT" ||
      smartApplyStatus === "BEFORE_PUBLISH" ||
      smartApplyStatus === "UNPUBLISHED");
  const publishModal = useModal();
  const copyModal = useModal();
  const openPreview = async () => {
    setIsLoading(true);
    const previewLink = await fetchPreviewUrl();
    window.open(previewLink, "_blank");
    setIsLoading(false);
  };
  const { auth } = useAuthContextV2();
  const procDetailsGroupPermissionAttached =
    new ProcedureDetailsGroupPermissionAttached(procedure);
  const canPublish =
    "publish" in perms &&
    auth.checkGroupPermission(procDetailsGroupPermissionAttached, [
      perms.publish,
    ]);
  const canUnPublish =
    "unPublish" in perms &&
    auth.checkGroupPermission(procDetailsGroupPermissionAttached, [
      perms.unPublish,
    ]);

  return (
    <div className="columns">
      <div className="column has-text-right buttons is-right">
        <Spinner loading={isLoading} />
        {currentStatus !== "ARCHIVED" && (
          <UrlCopyButton url={publishUrl} text="公開用URLをコピーする" />
        )}
        {currentStatus === "DRAFT" && (
          <SecondaryButton
            text="手続きプレビュー"
            icon="OpenInNewIcon"
            onClick={openPreview}
          />
        )}
        {"copy" in perms && (
          <RBACHidden requirePermissions={perms.copy}>
            <SecondaryButton
              text="コピーして新規作成する"
              onClick={copyModal.open}
            />
          </RBACHidden>
        )}
        {"unPublish" in perms && canUnPublish && (
          <SecondaryButton text="下書きに戻す" onClick={unPublish} />
        )}
        {"archive" in perms && (
          <RBACHidden requirePermissions={perms.archive}>
            <SecondaryButton text="アーカイブする" onClick={archive} />
          </RBACHidden>
        )}
        {"publish" in perms && canPublish && (
          <PrimaryButton
            text="公開する"
            onClick={isUsePublishModal ? publishModal.open : publish}
          />
        )}
      </div>
      {smartApply && isUsePublishModal && (
        <BaseModal
          isActive={publishModal.isActive}
          title="手続きマスタ公開前確認"
          onClose={publishModal.close}
          hasOverflow={true}
        >
          <PublishModal
            isServiceProviderAccount={isServiceProviderAccount}
            smartApply={smartApply}
            publish={publish}
            publishProcAndSmartApply={publishProcAndSmartApply}
            onClose={publishModal.close}
          />
        </BaseModal>
      )}
      <BaseModal
        isActive={copyModal.isActive}
        title="手続きのコピー"
        onClose={copyModal.close}
      >
        <ProcedureCopyModal
          orgId={orgId}
          name={!procName ? "" : procName}
          isServiceProviderAccount={isServiceProviderAccount}
          smartApply={smartApply}
          onCopy={copy}
          onClose={copyModal.close}
        />
      </BaseModal>
    </div>
  );
};

export default StatusControl;
